import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

// Componentes existentes
import Dashboard from '../components/Dashboard';
import Login from '../components/Login';
import LandingPage from '../components/LandingPage';
import ApiDocumentation from '../components/ApiDocumentation';

// Componentes de Descarga Masiva (existentes)
import ClientManagement from '../components/Clients/ClientManagement';
import DownloadManagement from '../components/Downloads/DownloadManagement';
import UserConsumption from '../components/Admin/UserConsumption';

// Componentes de Facturación
import InvoiceForm from '../components/Facturacion/InvoiceForm';
import BillingClientManagement from '../components/Facturacion/BillingClientManagement';
import BillingConfiguration from '../components/Facturacion/BillingConfiguration';

// Componentes de Administración
import UserManagement from '../components/Users/UserManagement';
import Reports from '../components/Admin/Reports';

// Componente placeholder para rutas en desarrollo
import ComingSoon from '../components/Placeholders/ComingSoon';

// Componente para rutas protegidas
const ProtectedRoute = ({ children, requiredPermissions = [] }) => {
  const { isAuthenticated, loading, hasAnyRole, userRole } = useAuth();
  
  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Si se especifican permisos requeridos, verificarlos
  if (requiredPermissions.length > 0) {
    const hasPermission = requiredPermissions.some(permission => {
      switch (permission) {
        case 'descarga_masiva':
          return hasAnyRole(['Usuario', 'Admin', 'SuperAdmin']);
        case 'facturacion':
          return hasAnyRole(['Usuario', 'Admin', 'SuperAdmin']);
        case 'facturacion_admin':
          return hasAnyRole(['Admin', 'SuperAdmin']);
        case 'admin':
          return hasAnyRole(['Admin', 'SuperAdmin']);
        case 'super_admin':
          return hasAnyRole(['SuperAdmin']);
        default:
          console.warn(`Permiso no reconocido: ${permission}`);
          return false;
      }
    });

    if (!hasPermission) {
      console.warn(`Usuario con rol '${userRole}' no tiene permisos para: ${requiredPermissions.join(', ')}`);
      return <Navigate to="/dashboard" replace />;
    }
  }

  return children;
};

const AppRoutes = () => {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      {/* Ruta de login */}
      <Route 
        path="/login" 
        element={
          isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />
        } 
      />
      
      {/* Dashboard principal */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />

      {/* === RUTAS DE DESCARGA MASIVA === */}
      <Route 
        path="/descarga-masiva/clientes" 
        element={
          <ProtectedRoute requiredPermissions={['descarga_masiva']}>
            <ClientManagement />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/descarga-masiva/descargas" 
        element={
          <ProtectedRoute requiredPermissions={['descarga_masiva']}>
            <DownloadManagement />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/descarga-masiva/consumos" 
        element={
          <ProtectedRoute requiredPermissions={['descarga_masiva']}>
            <UserConsumption />
          </ProtectedRoute>
        } 
      />

      {/* === RUTAS DE FACTURACIÓN === */}
      <Route 
        path="/facturacion/nueva" 
        element={
          <ProtectedRoute requiredPermissions={['facturacion']}>
            <InvoiceForm />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/facturacion/clientes" 
        element={
          <ProtectedRoute requiredPermissions={['facturacion']}>
            <BillingClientManagement />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/facturacion/configuracion" 
        element={
          <ProtectedRoute requiredPermissions={['facturacion_admin']}>
            <BillingConfiguration />
          </ProtectedRoute>
        } 
      />

      {/* === RUTAS DE ADMINISTRACIÓN === */}
      <Route 
        path="/admin/usuarios" 
        element={
          <ProtectedRoute requiredPermissions={['super_admin']}>
            <UserManagement />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/admin/reportes" 
        element={
          <ProtectedRoute requiredPermissions={['admin']}>
            <Reports />
          </ProtectedRoute>
        } 
      />

      {/* === RUTAS DE COMPATIBILIDAD (para mantener enlaces existentes) === */}
      <Route path="/clients" element={<Navigate to="/descarga-masiva/clientes" replace />} />
      <Route path="/downloads" element={<Navigate to="/descarga-masiva/descargas" replace />} />
      <Route path="/invoices" element={<Navigate to="/descarga-masiva/consumos" replace />} />
      <Route path="/users" element={<Navigate to="/admin/usuarios" replace />} />

      {/* Ruta raíz - Landing Page */}
      <Route path="/" element={<LandingPage />} />
      
      {/* Ruta de documentación de API */}
      <Route path="/api-docs" element={<ApiDocumentation />} />
      
      {/* Ruta 404 */}
      <Route 
        path="*" 
        element={
          <div className="container">
            <div className="card" style={{ textAlign: 'center', padding: '60px 40px' }}>
              <div style={{ fontSize: '6rem', marginBottom: '20px', opacity: 0.3 }}>404</div>
              <h1 style={{ color: '#333', marginBottom: '15px' }}>Página no encontrada</h1>
              <p style={{ color: '#666', marginBottom: '30px' }}>
                La página que buscas no existe o ha sido movida.
              </p>
              <button
                className="btn btn-primary"
                onClick={() => window.location.href = '/dashboard'}
              >
                ← Volver al Dashboard
              </button>
            </div>
          </div>
        } 
      />
    </Routes>
  );
};

export default AppRoutes;